
import React, { useState } from 'react';
import { Card } from "@/components/ui/card";
import { MatchDetailsCard } from './matches/MatchDetailsCard';
import { Match } from './types/head-to-head';
import { Button } from "@/components/ui/button";
import { Home, Globe, Flag } from "lucide-react";

interface FormSectionProps {
  matches: Match[];
  teamName: string;
  isHomeTeam: boolean;
}

interface ProcessedMatch extends Match {
  id: string;
  isHome: boolean;
  isAway: boolean;
  result: 'W' | 'L' | 'D';
}

const FormSection: React.FC<FormSectionProps> = ({ matches, teamName, isHomeTeam }) => {
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);
  const [formFilter, setFormFilter] = useState<'all' | 'home' | 'away'>('all');

  // Group matches by season
  const seasonMatches = matches.reduce((acc, match) => {
    const season = match.seasonYear || '0';
    if (!acc[season]) {
      acc[season] = [];
    }
    // Only include matches where this team played
    if (match.HomeTeam === teamName || match.AwayTeam === teamName) {
      acc[season].push(match);
    }
    return acc;
  }, {} as Record<string, Match[]>);

  const sortedSeasons = Object.keys(seasonMatches).sort((a, b) => parseInt(a) - parseInt(b));

  const processedMatches = sortedSeasons.flatMap(season => {
    return seasonMatches[season].map((match, index) => {
      const isHome = match.HomeTeam === teamName;
      const isAway = match.AwayTeam === teamName;
      const homeScore = parseInt(match.FTHG);
      const awayScore = parseInt(match.FTAG);
      
      let result: 'W' | 'L' | 'D';
      if (isHome) {
        result = homeScore > awayScore ? 'W' : homeScore < awayScore ? 'L' : 'D';
      } else {
        result = awayScore > homeScore ? 'W' : awayScore < homeScore ? 'L' : 'D';
      }

      return {
        ...match,
        id: `${season}-${index}`,
        isHome,
        isAway,
        result,
      };
    }).sort((a, b) => {
      // Simple string comparison for dates exactly as they are
      return a.Date.localeCompare(b.Date);
    });
  });

  // Filter matches based on home/away selection
  const filteredMatches = processedMatches.filter(match => {
    if (formFilter === 'home') return match.isHome;
    if (formFilter === 'away') return match.isAway;
    return true; // 'all' shows everything
  });

  const getResultColor = (result: 'W' | 'L' | 'D') => {
    const baseColors = {
      'W': 'bg-green-500 text-white hover:bg-green-600',
      'L': 'bg-red-500 text-white hover:bg-red-600',
      'D': 'bg-gray-400 text-white hover:bg-gray-500'
    };
    
    return baseColors[result];
  };

  const handleFormResultClick = (match: ProcessedMatch) => {
    setSelectedMatchId(selectedMatchId === match.id ? null : match.id);
  };

  const selectedMatch = selectedMatchId 
    ? filteredMatches.find(match => match.id === selectedMatchId)
    : null;

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {teamName} Form
            </h3>
            <div className="flex gap-2">
              <Button
                variant={formFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFormFilter('all')}
                className="flex items-center gap-1"
              >
                <Globe size={16} /> All
              </Button>
              <Button
                variant={formFilter === 'home' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFormFilter('home')}
                className="flex items-center gap-1"
              >
                <Home size={16} /> Home
              </Button>
              <Button
                variant={formFilter === 'away' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFormFilter('away')}
                className="flex items-center gap-1"
              >
                <Flag size={16} /> Away
              </Button>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {filteredMatches.map((match) => (
              <button
                key={match.id}
                onClick={() => handleFormResultClick(match)}
                className={`w-10 h-10 flex items-center justify-center font-bold rounded transition-colors cursor-pointer 
                  ${getResultColor(match.result)}`}
              >
                {match.result}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {selectedMatch && (
        <MatchDetailsCard match={selectedMatch} />
      )}
    </div>
  );
};

export default FormSection;
